/**
 * 案件を当てた一覧で、案件が変えたものだけに絞るトグル。
 *
 * **案件を当てた一覧で、案件が変えたものだけを拾えるようにする**（Dhik 2026-09-29「PJモードのとき
 * 業務マップ、画面一覧の右上に全体を出すか変更されたものだけを出すかのトグルボタンをつけてほしい」）。
 * 何を「変更」と数えるかは一覧ごとに違うので、ここは絞り込みを持たず、押されたかを返すだけにする。
 *
 * **形は「変更前と比較」と同じ 1 つのボタンにする**（同日「業務マップのトグルボタンを変更前と比較
 * ボタンと同じ感じのトグルボタンにしてくれ。UIに一貫性がない」）。以前の「全体 ／ 変更のみ」の
 * 2 ボタンの並びはやめ、押すと起きることを文言に書く: 全体を出しているときは「変更したものだけ見る」、
 * 絞っているときは「全体を見る」。押下中（絞っているとき）は塗りと `aria-pressed` で示す。
 * 形は `StateToggleButton` が持ち、`DiffCompareToggle` と共有する。
 *
 * 見出しの行の右端に置く。既定は全体で、状態は置いた画面が持つ（URL に載せない）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件を当てた一覧の見出しの右端に、変更したものだけに絞るか全体に戻すかを押すと起きる文言で示すトグルを描く。
 * @graph-connects none
 */
import { useTranslation } from 'react-i18next';

import { StateToggleButton } from './StateToggleButton.js';

/** `PjChangedOnlyToggle` の props。 */
export interface PjChangedOnlyToggleProps {
  /** いま変更したものだけに絞っているか。 */
  changedOnly: boolean;
  /** 押したときに呼ぶ。絞るなら `true`、全体に戻すなら `false`。 */
  onChange: (changedOnly: boolean) => void;
}

/**
 * 変更したものだけに絞る / 全体に戻すトグルのボタン。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件を当てた一覧で、変更したものだけに絞るか全体に戻すかを 1 つのトグルボタンで切り替える。
 * @graph-connects StateToggleButton [calls] 押し下げで文言を入れ替えるボタン
 */
export function PjChangedOnlyToggle({ changedOnly, onChange }: PjChangedOnlyToggleProps) {
  const { t } = useTranslation();

  return (
    <StateToggleButton
      pressed={changedOnly}
      idleLabel={t('pjScope.showChangedOnly')}
      pressedLabel={t('pjScope.showAll')}
      onToggle={() => {
        onChange(!changedOnly);
      }}
      data-pj-changed-only-toggle
      className="ml-auto"
    />
  );
}
